import {
  useRef,
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
} from 'react'
import { gsap } from 'gsap'
import {
  faArrowsMaximize,
  faPlus,
  faRectangleHistory,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { Button } from '@/ui'
import { mtc } from '@/styles'
import { useIsDeviceWidth, DeviceWidthCombo } from '@/utils/deviceWidthHelpers'
import { ChatPanelHeader } from './ChatPanelHeader'
import { HeaderButton } from '@/features/adviser-chat/components/HeaderButton'
import {
  slideIn,
  slideOut,
  animateCompact,
  animateNewConversation,
} from './panelAnimations'
import type { ActiveView } from '../lib/types'
import '../adviser-chat.scss'

interface ChatPanelProps {
  open: boolean
  title?: string
  compact?: boolean
  onClose: () => void
  onExpand?: () => void
  onSetView: (view: ActiveView) => void
  onNewConversation?: () => void
  onClosed?: () => void
  inputArea?: React.ReactNode
  children: React.ReactNode
}

const MIN_WIDTH = 420
const MAX_WIDTH_VW = 45
const DEFAULT_WIDTH = 'clamp(420px, 34vw, 560px)'

export const ChatPanel = ({
  open,
  title,
  compact,
  onClose,
  onExpand,
  onSetView,
  onNewConversation,
  onClosed,
  inputArea,
  children,
}: ChatPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const headerWrapperRef = useRef<HTMLDivElement>(null)
  const contentSectionRef = useRef<HTMLDivElement>(null)
  const buttonsRowRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLDivElement>(null)
  const inputAreaRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const innerContentRef = useRef<HTMLDivElement>(null)
  const [panelWidth, setPanelWidth] = useState<number | null>(null)
  const isResizing = useRef(false)
  const isMobile = useIsDeviceWidth(DeviceWidthCombo.mobileOrTablet)

  // Slide out when closing, then notify parent to unmount
  useEffect(() => {
    if (open || !panelRef.current) return
    slideOut(panelRef.current, onClosed)
  }, [open, onClosed])

  // Slide in from the right on mount
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (!panelRef.current) return
      slideIn(panelRef.current)
    })
    return () => ctx.revert()
  }, [])

  // Compact: collapse buttons row + shrink header padding + fade close button
  useEffect(() => {
    animateCompact(
      {
        headerWrapper: headerWrapperRef.current,
        contentSection: contentSectionRef.current,
        buttonsRow: buttonsRowRef.current,
        closeButton: closeButtonRef.current,
        inputArea: inputAreaRef.current,
      },
      Boolean(compact)
    )
  }, [compact])

  // Escape key closes panel
  useEffect(() => {
    if (!open) return undefined
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  const handleNewConversationAnimated = useCallback(() => {
    const inner = innerContentRef.current
    const card = cardRef.current
    if (!inner || !card) {
      onNewConversation?.()
      return
    }
    animateNewConversation(inner, card, () => onNewConversation?.())
  }, [onNewConversation])

  // Resize handle — drag left edge
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isResizing.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return
      const maxWidth = window.innerWidth * (MAX_WIDTH_VW / 100)
      setPanelWidth(
        Math.max(MIN_WIDTH, Math.min(maxWidth, window.innerWidth - ev.clientX))
      )
    }
    const handleMouseUp = () => {
      isResizing.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [])

  return (
    <div
      ref={panelRef}
      className={mtc(
        'adviser-panel',
        'flex flex-col overflow-hidden',
        isMobile
          ? 'fixed inset-0 z-50 w-full h-full'
          : 'relative h-full flex-shrink-0 rounded-tl-[24px] rounded-bl-[24px] border-l border-neutral-100'
      )}
      style={
        isMobile
          ? undefined
          : {
              width: panelWidth ?? DEFAULT_WIDTH,
              minWidth: MIN_WIDTH,
              maxWidth: `${MAX_WIDTH_VW}vw`,
            }
      }
    >
      {/* Resize handle — left edge (desktop only) */}
      {!isMobile && (
        <div
          role="slider"
          aria-label="Resize panel"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={window.innerWidth * (MAX_WIDTH_VW / 100)}
          aria-valuenow={panelWidth ?? panelRef.current?.offsetWidth}
          tabIndex={0}
          onMouseDown={handleResizeStart}
          onKeyDown={(e) => {
            const max = window.innerWidth * (MAX_WIDTH_VW / 100)
            if (e.key === 'ArrowLeft')
              setPanelWidth((w) =>
                Math.min(
                  (w ?? panelRef.current?.offsetWidth ?? MIN_WIDTH) + 20,
                  max
                )
              )
            if (e.key === 'ArrowRight')
              setPanelWidth((w) =>
                Math.max(
                  (w ?? panelRef.current?.offsetWidth ?? MIN_WIDTH) - 20,
                  MIN_WIDTH
                )
              )
          }}
          className="absolute top-0 left-0 w-1.5 h-full cursor-col-resize z-10 hover:bg-moss-200/20 transition-colors"
        />
      )}

      {/* Close button — hidden in compact (compact header shows xmark inline) */}
      <div
        ref={closeButtonRef}
        className="absolute top-4 right-3 z-20 flex items-center"
      >
        {onExpand && (
          <HeaderButton
            icon={faArrowsMaximize}
            label="Open full screen"
            onClick={onExpand}
          />
        )}
        <HeaderButton icon={faXmark} label="Close chat" onClick={onClose} />
      </div>

      {/* Header */}
      <div ref={headerWrapperRef} className="relative z-10 pt-6">
        <ChatPanelHeader
          title={title}
          compact={compact}
          onNew={handleNewConversationAnimated}
          onHistory={() => onSetView('history')}
          onExpand={onExpand}
          onClose={onClose}
        />
      </div>

      {/* Ghost buttons + white card + input */}
      <div
        ref={contentSectionRef}
        className={mtc(
          'relative z-10 flex flex-col flex-1 min-h-0 gap-3 mt-4',
          !inputArea && 'pb-3'
        )}
      >
        {/* Buttons row — collapses to height 0 in compact mode */}
        <div
          ref={buttonsRowRef}
          className="flex items-center justify-between px-6 shrink-0 overflow-hidden h-10"
        >
          <Button
            variant="secondary"
            size="sm"
            iconLeft={faPlus}
            onClick={handleNewConversationAnimated}
            className="adviser-new-conversation-btn"
          >
            New conversation
          </Button>
          <HeaderButton
            icon={faRectangleHistory}
            label="History"
            onClick={() => onSetView('history')}
            showLabel
          />
        </div>

        {/* White card — intentionally not using the design system Card component
            because this is a full-height flex container with GSAP-cloned content.
            Card adds my-4 margin and uses bg-card which breaks the flex layout. */}
        <div
          ref={cardRef}
          className="adviser-panel-card [view-transition-name:haven-card] relative flex flex-col flex-1 min-h-0 rounded-[24px] overflow-hidden bg-neutral-0"
        >
          <div ref={innerContentRef} className="flex flex-col flex-1 min-h-0">
            {children}
          </div>
        </div>

        {/* Input area */}
        {inputArea && (
          <div ref={inputAreaRef} className="shrink-0 px-6 pb-2 mt-2">
            {inputArea}
          </div>
        )}
      </div>
    </div>
  )
}
